import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ArrowLeft, Users, Mail, Camera, TrendingUp, DollarSign, Gift, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import AppHeader from '../components/AppHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPanel() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'admin') fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true })
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    }
    setLoading(false);
  };

  const updateSubscription = async (userId, tier) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/subscription`, { tier }, { withCredentials: true });
      toast.success('Subscription updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-600">Admin access required</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F3]">
      <AppHeader />
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <Link to="/dashboard">
          <Button variant="ghost" size="sm" className="mb-4" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Dashboard
          </Button>
        </Link>
        <h1 className="text-4xl font-light tracking-tight mb-8" style={{fontFamily: 'Spectral, serif'}}>Business Metrics</h1>

        {stats && (
          <>
            {/* Revenue metrics */}
            <div className="mb-8">
              <p className="label-text mb-4">Revenue</p>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
                  <DollarSign className="w-6 h-6 text-[#B94A3E] mb-2" strokeWidth={1.5} />
                  <div className="text-3xl font-light" style={{fontFamily: 'Spectral, serif'}} data-testid="stat-mrr">${stats.mrr}</div>
                  <span className="label-text">Monthly Recurring Revenue</span>
                </div>
                <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
                  <TrendingUp className="w-6 h-6 text-[#B94A3E] mb-2" strokeWidth={1.5} />
                  <div className="text-3xl font-light" style={{fontFamily: 'Spectral, serif'}} data-testid="stat-arr">${stats.arr.toLocaleString()}</div>
                  <span className="label-text">Annual Run Rate</span>
                </div>
                <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
                  <Zap className="w-6 h-6 text-[#B94A3E] mb-2" strokeWidth={1.5} />
                  <div className="text-3xl font-light" style={{fontFamily: 'Spectral, serif'}} data-testid="stat-conversion">{stats.conversion_rate}%</div>
                  <span className="label-text">Trial → Paid Conversion</span>
                </div>
              </div>
            </div>

            {/* User metrics */}
            <div className="mb-8">
              <p className="label-text mb-4">Users</p>
              <div className="grid md:grid-cols-4 gap-6">
                <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
                  <Users className="w-6 h-6 text-[#B94A3E] mb-2" strokeWidth={1.5} />
                  <div className="text-3xl font-light" style={{fontFamily: 'Spectral, serif'}} data-testid="stat-total-users">{stats.total_users}</div>
                  <span className="label-text">Total Users</span>
                </div>
                <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
                  <Users className="w-6 h-6 text-[#B94A3E] mb-2" strokeWidth={1.5} />
                  <div className="text-3xl font-light" style={{fontFamily: 'Spectral, serif'}} data-testid="stat-trial">{stats.trial_users}</div>
                  <span className="label-text">On Trial</span>
                </div>
                <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
                  <Users className="w-6 h-6 text-[#B94A3E] mb-2" strokeWidth={1.5} />
                  <div className="text-3xl font-light" style={{fontFamily: 'Spectral, serif'}} data-testid="stat-paid">{stats.paid_users}</div>
                  <span className="label-text">Paid Users</span>
                </div>
                <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
                  <TrendingUp className="w-6 h-6 text-[#B94A3E] mb-2" strokeWidth={1.5} />
                  <div className="text-3xl font-light" style={{fontFamily: 'Spectral, serif'}} data-testid="stat-new-30d">+{stats.new_users_30d}</div>
                  <span className="label-text">New Last 30 Days</span>
                </div>
              </div>
            </div>

            {/* Engagement metrics */}
            <div className="mb-8">
              <p className="label-text mb-4">Engagement</p>
              <div className="grid md:grid-cols-4 gap-6">
                <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
                  <Mail className="w-6 h-6 text-[#B94A3E] mb-2" strokeWidth={1.5} />
                  <div className="text-3xl font-light" style={{fontFamily: 'Spectral, serif'}} data-testid="stat-letters">{stats.total_letters}</div>
                  <span className="label-text">Total Letters</span>
                </div>
                <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
                  <Mail className="w-6 h-6 text-[#B94A3E] mb-2" strokeWidth={1.5} />
                  <div className="text-3xl font-light" style={{fontFamily: 'Spectral, serif'}} data-testid="stat-mailed">{stats.letters_mailed}</div>
                  <span className="label-text">Letters Mailed</span>
                </div>
                <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
                  <Camera className="w-6 h-6 text-[#B94A3E] mb-2" strokeWidth={1.5} />
                  <div className="text-3xl font-light" style={{fontFamily: 'Spectral, serif'}} data-testid="stat-photos">{stats.total_photos}</div>
                  <span className="label-text">Total Photos</span>
                </div>
                <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
                  <TrendingUp className="w-6 h-6 text-[#B94A3E] mb-2" strokeWidth={1.5} />
                  <div className="text-3xl font-light" style={{fontFamily: 'Spectral, serif'}} data-testid="stat-letters-30d">+{stats.letters_30d}</div>
                  <span className="label-text">Letters Last 30 Days</span>
                </div>
              </div>
            </div>

            {/* Growth loop metrics */}
            <div className="mb-8">
              <p className="label-text mb-4">Growth Loop</p>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
                  <Gift className="w-6 h-6 text-[#B94A3E] mb-2" strokeWidth={1.5} />
                  <div className="text-3xl font-light" style={{fontFamily: 'Spectral, serif'}} data-testid="stat-total-referrals">{stats.total_referrals}</div>
                  <span className="label-text">Users From Referrals</span>
                </div>
                <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
                  <Gift className="w-6 h-6 text-[#B94A3E] mb-2" strokeWidth={1.5} />
                  <div className="text-3xl font-light" style={{fontFamily: 'Spectral, serif'}} data-testid="stat-active-referrers">{stats.users_with_referrals}</div>
                  <span className="label-text">Active Referrers</span>
                </div>
                <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
                  <Zap className="w-6 h-6 text-[#B94A3E] mb-2" strokeWidth={1.5} />
                  <div className="text-3xl font-light" style={{fontFamily: 'Spectral, serif'}} data-testid="stat-viral-coefficient">
                    {stats.total_users > 0 ? (stats.total_referrals / stats.total_users).toFixed(2) : '0.00'}
                  </div>
                  <span className="label-text">Viral Coefficient (k)</span>
                </div>
              </div>
            </div>

            {/* Tier breakdown */}
            <div className="mb-8 bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
              <p className="label-text mb-4">Paid Tier Breakdown</p>
              <div className="grid grid-cols-3 gap-6">
                {['basic', 'plus', 'premium'].map(tier => (
                  <div key={tier} className="text-center">
                    <div className="text-3xl font-light text-[#B94A3E]" style={{fontFamily: 'Spectral, serif'}} data-testid={`tier-${tier}-count`}>{stats.tier_breakdown[tier] || 0}</div>
                    <span className="label-text capitalize">{tier}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="bg-white border border-[#E5E0D8] rounded-md p-8 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
          <h3 className="text-2xl font-light tracking-tight mb-6" style={{fontFamily: 'Spectral, serif'}}>Users</h3>
          {loading ? (
            <p className="text-[#737373]">Loading...</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E5E0D8]">
                    <th className="text-left py-3 label-text">Email</th>
                    <th className="text-left py-3 label-text">Name</th>
                    <th className="text-left py-3 label-text">Role</th>
                    <th className="text-left py-3 label-text">Tier</th>
                    <th className="text-left py-3 label-text">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u._id} className="border-b border-[#E5E0D8]" data-testid={`user-row-${u._id}`}>
                      <td className="py-4 text-sm">{u.email}</td>
                      <td className="py-4 text-sm">{u.name}</td>
                      <td className="py-4 text-sm">{u.role}</td>
                      <td className="py-4 text-sm">{u.subscription_tier || 'none'}</td>
                      <td className="py-4">
                        <Select value={u.subscription_tier || 'none'} onValueChange={(val) => updateSubscription(u._id, val === 'none' ? null : val)}>
                          <SelectTrigger className="w-32 border-[#E5E0D8]" data-testid={`tier-select-${u._id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="trial">Trial</SelectItem>
                            <SelectItem value="basic">Basic</SelectItem>
                            <SelectItem value="plus">Plus</SelectItem>
                            <SelectItem value="premium">Premium</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
